import "./Music.css";
import SocialLinks from "../../components/SocialLinks/SocialLinks";
import { SoftwareButton } from "../../components/Buttons/Buttons";
import { useGoogleFonts, LEGACY_MUSIC_FONTS } from "../../utils/loadFonts";
import Releases from "./sections/Releases/Releases";
import Gallery from "./sections/Gallery/Gallery";
import useScrollMemory from "../../utils/useScrollMemory";
import SiteLogo from "../../components/SiteLogo/SiteLogo";
import LiveClock from "../../components/LiveClock/LiveClock";
import InfoPanel from "../../components/InfoPanel/InfoPanel";
import { useLocation, useNavigate } from "react-router-dom";
import CustomCursor from "../../components/CustomCursor/CustomCursor";
import ScrollIndicator from "../../components/ScrollIndicator/ScrollIndicator";
import useMagnetic from "../../utils/useMagnetic";
import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";

// The /legacy/music snapshot's sections: downloaded only when it's opened
const MusicHero = lazy(() => import("./sections/Hero/Hero"));
const Songs = lazy(() => import("./sections/Songs/Songs"));
// (its fonts too)
function LegacyFonts() {
  useGoogleFonts(LEGACY_MUSIC_FONTS);
  return null;
}

const TOP_BAR_MIN_GAP = 16; // smallest gap allowed between the logo and the clock/Contact
// Addresses that open the About / Contact panel over the home page
const PANEL_PATHS = { "/about": "about", "/contact": "contact" };
const TOP_LINKS_GAP = 18; // space between About and Contact (.music-menu-items gap in Music.css)

function Music({ legacy = false }) {
  // Opening/closing About or Contact changes the address but not the page,
  // so the scroll position stays where it is
  useScrollMemory({ stayOnChange: true });
  useMagnetic(); // buttons with data-magnetic lean toward the mouse
  const [ref, inView] = useInView({
    threshold: 0.015,
  });

  // Top bar layout, measured (not a fixed screen width) so it also works with
  // larger text settings. Clock on the left, logo centered, and on the right:
  // - About and Contact side by side, if they fit
  // - otherwise (phones) one MENU button that drops them down under it
  // If even that doesn't fit, everything goes on centered lines.
  const topBarRef = useRef(null);
  const menuButtonRef = useRef(null);

  // Once the Releases panel reaches the top bar, the bar gets a dark,
  // frosted background so its text doesn't sit on top of the releases'
  // (over the gallery photos it stays see-through)
  const [overReleases, setOverReleases] = useState(false);
  useEffect(() => {
    if (legacy) return undefined;
    let frame = null;
    const check = () => {
      frame = null;
      const releases = document.querySelector(".releases");
      const bar = topBarRef.current;
      if (!releases || !bar) return;
      setOverReleases(releases.getBoundingClientRect().top < bar.getBoundingClientRect().bottom + 8);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [legacy]);
  const [compact, setCompact] = useState(false); // MENU instead of About + Contact
  const [stacked, setStacked] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useLayoutEffect(() => {
    const bar = topBarRef.current;
    if (!bar) return undefined;
    const measure = () => {
      const width = (el) => el?.getBoundingClientRect().width || 0;
      const style = getComputedStyle(bar);
      const room = bar.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const logo = width(bar.querySelector(".music-logo-bar .site-logo"));
      const clock = width(bar.querySelector(".music-clock"));
      const links = [...bar.querySelectorAll(".music-menu-items .music-contact-button")].map(width);
      const linksInRow = links.reduce((sum, w) => sum + w, 0) + TOP_LINKS_GAP * (links.length - 1);
      const menu = width(menuButtonRef.current);
      // Logo in the middle: each side needs space for the wider side
      const fits = (right) => logo + 2 * (Math.max(clock, right) + TOP_BAR_MIN_GAP) <= room;
      const useMenu = !fits(linksInRow);
      setCompact(useMenu);
      setStacked(!fits(useMenu ? menu : linksInRow));
      if (!useMenu) setMenuOpen(false);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    document.fonts?.ready.then(measure); // re-check once the fonts have loaded
    return () => observer.disconnect();
  }, [legacy]);

  // The open menu closes with Escape or a tap anywhere else
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event) => {
      if (!event.target.closest?.(".music-top-links")) setMenuOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // While the menu is open, the bar's frosted background (over the releases)
  // reaches down far enough to cover About and Contact too: how far is
  // measured, so it also fits larger text settings
  useLayoutEffect(() => {
    const bar = topBarRef.current;
    if (!bar) return;
    const items = bar.querySelector(".music-menu-items");
    const reach =
      menuOpen && compact && items
        ? Math.max(0, items.getBoundingClientRect().bottom - bar.getBoundingClientRect().bottom)
        : 0;
    bar.style.setProperty("--menu-reach", `${Math.ceil(reach)}px`);
  }, [menuOpen, compact]);

  // The About / Contact panel has its own addresses, /about and /contact, so
  // they can be shared, and the browser's Back button closes the panel (or
  // reopens it when coming back from a song opened in About).
  const location = useLocation();
  const navigate = useNavigate();
  const panelTab = legacy ? null : PANEL_PATHS[location.pathname] ?? null;
  const openedHere = Boolean(location.state?.panelFromHome);
  const openPanel = useCallback(
    (tab) =>
      panelTab
        ? // Switching tabs: just update the address (no extra Back steps)
          navigate(`/${tab}`, { replace: true, state: location.state })
        : navigate(`/${tab}`, { state: { panelFromHome: true } }),
    [panelTab, navigate, location.state]
  );
  const closePanel = useCallback(
    () =>
      openedHere
        ? navigate(-1) // back to the home page entry it was opened from
        : navigate("/", { replace: true }), // arrived straight at /about or /contact
    [openedHere, navigate]
  );

  // Picking About/Contact from the menu: close the menu first, and move focus
  // to MENU so it comes back there when the panel closes
  const choose = (tab) => {
    if (compact) {
      setMenuOpen(false);
      menuButtonRef.current?.focus();
    }
    openPanel(tab);
  };

  const scrollToTop = () => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
  };

  return (
    <div className="container music-container">
      {!legacy ? (
        <header ref={topBarRef} className={`music-top-bar ${stacked ? "is-stacked" : ""} ${compact ? "has-menu" : ""} ${
            overReleases ? "is-over-releases" : ""
          }`}
        >
          {/* Left: visitor's local time · center: logo · right: About and Contact */}
          <div className="music-top-left">
            <LiveClock className="music-clock" />
          </div>
          <div className="music-logo-bar">
            <SiteLogo onClick={scrollToTop} label="Back to top" />
          </div>
          <div className="music-top-right">
            <nav className={`music-top-links ${menuOpen ? "is-open" : ""}`} aria-label="Site">
              {/* Phones: one MENU button, underlined while the menu is open */}
              <button
                ref={menuButtonRef}
                className="music-contact-button music-menu-button"
                onClick={() => setMenuOpen((open) => !open)}
                aria-expanded={menuOpen}
                aria-controls="music-menu-items"
                aria-hidden={!compact}
                tabIndex={compact ? 0 : -1}
              >
                Menu
              </button>
              <div id="music-menu-items" className="music-menu-items">
                <button
                  className="music-contact-button"
                  onClick={() => choose("about")}
                  aria-haspopup="dialog"
                  tabIndex={compact && !menuOpen ? -1 : 0}
                >
                  About
                </button>
                <button
                  className="music-contact-button"
                  onClick={() => choose("contact")}
                  aria-haspopup="dialog"
                  tabIndex={compact && !menuOpen ? -1 : 0}
                >
                  Contact
                </button>
              </div>
            </nav>
          </div>
        </header>
      ) : null}
      {legacy ? (
        <div className="button-bar music-button-bar">
          <SoftwareButton white withText={false} filled />
        </div>
      ) : null}
      {legacy ? <LegacyFonts /> : null}
      {/* The /legacy/music snapshot keeps the original scroll animation */}
      {legacy ? (
        <Suspense fallback={null}>
          <MusicHero legacy />
        </Suspense>
      ) : (
        <Gallery />
      )}
      {/* <NewRelease /> */}
      {/* The /legacy/music snapshot keeps the original releases design */}
      {legacy ? (
        <Suspense fallback={null}>
          <Songs containerRef={ref} legacy />
        </Suspense>
      ) : (
        <Releases ref={ref} />
      )}
      <SocialLinks
        inverted={inView}
        white
        music
        position="right"
        fixed
        className={legacy ? "" : "music-follow"}
        // SoundCloud only on the Contact panel
        exclude={legacy ? [] : ["soundcloud"]}
      />
      {!legacy ? <InfoPanel tab={panelTab} onTab={openPanel} onClose={closePanel} /> : null}
      {!legacy ? <CustomCursor /> : null}
      {/* Home page: drawn as a line with ^ and v at its ends */}
      <ScrollIndicator arrows={!legacy} />
    </div>
  );
}

export default Music;
